/** Seed preset determines which seed steps run after protocol deployment. */
export type SeedPreset = "full-stack" | "quick" | "vault-dev";

/** Mining mode after deployment. */
export type BlockTimeMode = "instant" | "2s" | "12s";

/** ETH balance per test account (whole ETH). */
export type FundAmount = 1 | 10 | 100;

/** Options for "Start Fresh" mode. */
export interface FreshModeOptions {
  seedPreset: SeedPreset;
  blockTime: BlockTimeMode;
}

/** Options for "Fork Ethereum" / "Fork Base" modes. */
export interface ForkModeOptions {
  forkBlock: "latest" | bigint;
  fundAmount: FundAmount;
  blockTime: BlockTimeMode;
}

/** Options for "Custom RPC" mode. */
export interface CustomRpcOptions {
  forkBlock: "latest" | bigint;
  blockTime: BlockTimeMode;
}

export const DEFAULT_FRESH_OPTIONS: FreshModeOptions = {
  seedPreset: "full-stack",
  blockTime: "instant",
};

export const DEFAULT_FORK_OPTIONS: ForkModeOptions = {
  forkBlock: "latest",
  fundAmount: 10,
  blockTime: "instant",
};

export const DEFAULT_CUSTOM_OPTIONS: CustomRpcOptions = {
  forkBlock: "latest",
  blockTime: "instant",
};

/** Maps seed preset to which seed steps to run. */
export const SEED_PRESET_STEPS: Record<
  SeedPreset,
  {
    tokens: boolean;
    liquidity: boolean;
    swaps: boolean;
    agents: boolean;
    vaults: boolean;
    deposits: boolean;
  }
> = {
  "full-stack": {
    tokens: true,
    liquidity: true,
    swaps: true,
    agents: true,
    vaults: true,
    deposits: true,
  },
  quick: {
    tokens: false,
    liquidity: false,
    swaps: false,
    agents: false,
    vaults: false,
    deposits: false,
  },
  "vault-dev": {
    tokens: true,
    liquidity: false,
    swaps: false,
    agents: true,
    vaults: true,
    deposits: true,
  },
};

/** Estimate boot time based on seed preset. */
export function estimateBootTime(preset: SeedPreset): string {
  switch (preset) {
    case "full-stack":
      return "~15s";
    case "quick":
      return "~5s";
    case "vault-dev":
      return "~10s";
  }
}

/** Maps BlockTimeMode to mining interval in ms (0 = automine). */
export function blockTimeModeToInterval(mode: BlockTimeMode): number {
  switch (mode) {
    case "instant":
      return 0;
    case "2s":
      return 2000;
    case "12s":
      return 12000;
  }
}
